import React, { useMemo, useState } from 'react';
import { Modal, Typography, List, Button, Tag, Alert } from 'antd';
import {
  EnvironmentOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { IStoreLocation } from '../types/storefront.types';
import {
  isLocationOpen,
  formatWeeklySchedule,
  formatTodayHours,
  getLocationStatusMessage,
} from '../utils/storeSchedule';

const { Text } = Typography;

interface LocationStatusModalProps {
  open: boolean;
  onClose: () => void;
  locations: IStoreLocation[];
  selectedLocationId?: string;
  onSelect: (location: IStoreLocation) => void;
}

export const LocationStatusModal: React.FC<LocationStatusModalProps> = ({
  open,
  onClose,
  locations,
  selectedLocationId,
  onSelect,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const locationsWithStatus = useMemo(() => {
    return locations.map((loc) => {
      const isCurrentlyOpen = isLocationOpen(loc);
      const status = getLocationStatusMessage(loc);
      const todayHours = loc.todayHours ?? formatTodayHours(loc);
      const weekly = formatWeeklySchedule(loc.operatingSchedules);
      const hasNewSchedule = !!(loc.operatingSchedules && loc.operatingSchedules.length > 0);
      return {
        ...loc,
        isCurrentlyOpen,
        status,
        todayHours,
        weekly,
        hasNewSchedule,
      };
    });
  }, [locations]);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EnvironmentOutlined style={{ color: 'var(--color-saffron)', fontSize: 20 }} />
          <Text strong style={{ fontSize: 16, margin: 0, fontFamily: 'var(--font-display)' }}>
            Nuestras Sucursales
          </Text>
          <Tag style={{ marginLeft: 6, fontSize: 11, borderRadius: 999 }}>{locations.length}</Tag>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      style={{ borderRadius: 'var(--radius-xl)' }}
      styles={{ body: { padding: 16, maxHeight: '70dvh', overflowY: 'auto' } }}
      width={520}
    >
      <List
        dataSource={locationsWithStatus}
        rowKey="id"
        renderItem={(loc) => {
          const isSelected = selectedLocationId === loc.id;
          const isOpen = loc.isCurrentlyOpen;
          const isExpanded = expandedId === loc.id;

          return (
            <div
              style={{
                padding: 12,
                borderRadius: 'var(--radius-md)',
                border: isSelected
                  ? '2px solid var(--color-saffron)'
                  : '1px solid var(--color-border)',
                backgroundColor: isSelected ? 'var(--color-saffron-soft)' : 'var(--color-card)',
                marginBottom: 8,
                transition: 'all var(--duration-normal) var(--ease-out)',
              }}
            >
              {/* Header row clickable for selection */}
              <div
                onClick={() => onSelect(loc)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  gap: 12,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Text
                      strong
                      style={{ fontSize: 15, color: 'var(--color-ink)', display: 'block' }}
                    >
                      {loc.name}
                    </Text>
                    <Tag
                      color={isOpen ? 'success' : 'error'}
                      style={{ fontSize: 10, borderRadius: 'var(--radius-sm)', margin: 0 }}
                    >
                      {isOpen ? (
                        <>
                          <CheckCircleOutlined style={{ marginRight: 4 }} />
                          Abierto
                        </>
                      ) : (
                        <>
                          <CloseCircleOutlined style={{ marginRight: 4 }} />
                          Cerrado
                        </>
                      )}
                    </Tag>
                  </div>
                  <Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>
                    {loc.address}
                  </Text>

                  {/* Hoy */}
                  <div
                    style={{
                      marginTop: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      fontFamily: 'var(--font-mono)',
                      color: isOpen ? 'var(--color-accent)' : 'var(--color-error)',
                    }}
                  >
                    <ClockCircleOutlined style={{ fontSize: 11 }} />
                    Hoy: {loc.todayHours || '—'}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    alignItems: 'flex-end',
                    flexShrink: 0,
                  }}
                >
                  {isSelected ? (
                    <Tag
                      color="warning"
                      icon={<CheckOutlined />}
                      style={{
                        borderRadius: 'var(--radius-full)',
                        padding: '4px 10px',
                        fontWeight: 700,
                      }}
                    >
                      Activa
                    </Tag>
                  ) : (
                    <Button
                      size="small"
                      type="default"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(loc);
                      }}
                      style={{ borderRadius: 'var(--radius-full)', fontSize: 12 }}
                    >
                      Seleccionar
                    </Button>
                  )}
                  {loc.hasNewSchedule && (
                    <Button
                      size="small"
                      type="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId(isExpanded ? null : loc.id);
                      }}
                      style={{ fontSize: 11, padding: '0 6px', height: 22 }}
                    >
                      {isExpanded ? 'Ocultar horario' : 'Ver horario'}
                    </Button>
                  )}
                </div>
              </div>

              {/* Horario semanal expandido */}
              {isExpanded && loc.hasNewSchedule && (
                <div
                  style={{
                    marginTop: 12,
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <ClockCircleOutlined style={{ color: 'var(--color-accent)', fontSize: 12 }} />
                    <Text strong style={{ fontSize: 12, fontFamily: 'var(--font-display)' }}>
                      Horario semanal
                    </Text>
                    {loc.timezone && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ({loc.timezone})
                      </Text>
                    )}
                  </div>
                  <div
                    style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 12px' }}
                  >
                    {loc.weekly.map((d) => (
                      <React.Fragment key={d.day}>
                        <Text
                          style={{
                            fontSize: 12,
                            fontFamily: 'var(--font-body)',
                            fontWeight: 600,
                            color: d.isClosed ? 'var(--color-muted)' : 'var(--color-ink)',
                          }}
                        >
                          {d.label}
                        </Text>
                        <Text
                          style={{
                            fontSize: 12,
                            fontFamily: 'var(--font-mono)',
                            textAlign: 'right',
                            color: d.isClosed ? 'var(--color-error)' : 'var(--color-ink-soft)',
                            fontWeight: d.isClosed ? 600 : 500,
                          }}
                        >
                          {d.hours}
                        </Text>
                      </React.Fragment>
                    ))}
                  </div>
                  {!loc.isOpenForOrders && (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginTop: 10, padding: '6px 10px' }}
                      message={
                        <span style={{ fontSize: 11, fontWeight: 600 }}>
                          La sucursal actualmente no está recibiendo pedidos
                        </span>
                      }
                      description={
                        <span style={{ fontSize: 11 }}>Volverá a estar disponible pronto.</span>
                      }
                    />
                  )}
                  {/* Fallback legacy si no hay weekly pero hay openingTime */}
                  {loc.weekly.every((w) => w.isClosed) && (loc.openingTime || loc.closingTime) && (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                      Horario legacy: {loc.openingTime || '--'} - {loc.closingTime || '--'}
                    </Text>
                  )}
                </div>
              )}

              {/* Si no tiene nuevo schedule, mostrar legacy simple */}
              {!loc.hasNewSchedule && !isExpanded && (loc.openingTime || loc.closingTime) && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ClockCircleOutlined style={{ color: 'var(--color-saffron)', fontSize: 11 }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {loc.openingTime ? `Abre: ${loc.openingTime}` : ''}
                    {loc.openingTime && loc.closingTime ? ' - ' : ''}
                    {loc.closingTime ? `Cierra: ${loc.closingTime}` : ''}
                    {loc.timezone && ` (${loc.timezone})`}
                  </Text>
                </div>
              )}
              {!loc.hasNewSchedule && !loc.openingTime && !loc.closingTime && (
                <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: 'block' }}>
                  Sin horario definido
                </Text>
              )}
            </div>
          );
        }}
      />
    </Modal>
  );
};
